import React, { useMemo, useState } from 'react';
import { Clock, CheckCircle2, XCircle, Send, Ban } from 'lucide-react';
import ENTITY_ICONS from '../components/common/entityIcons';
import invitationsService from '../services/invitations';
import { splitRoles } from '../utils/roleFlags';
import { useApps } from '../contexts/AppsContext';
import { useNotice } from '../components/common/Notices';
import { useConfirm } from '../components/common/ConfirmDialog';
import { Button } from '../components/ui/Button';
import PageHeader from '../components/common/PageHeader';
import SkeletonList from '../components/common/SkeletonList';
import Modal from '../components/common/Modal';
import DataTable, { RowAction, RowActions } from '../components/common/DataTable';
import StatusBadge from '../components/common/StatusBadge';
import useServerTable from '../hooks/useServerTable';
import useRoles from '../hooks/useRoles';
import useTenants from '../hooks/useTenants';
import { getToken } from '../utils/storage';
import { decodeJwtPayload } from '../utils/jwt';
import { Field } from '../components/ui/Field';
import { TextInput, Textarea, Select } from '../components/ui/Input';

const UsersIcon = ENTITY_ICONS.users;

const statusConfig = {
    pending: { icon: Clock, label: 'Pending' },
    accepted: { icon: CheckCircle2, label: 'Accepted' },
    expired: { icon: Clock, label: 'Expired' },
    revoked: { icon: XCircle, label: 'Revoked' },
};

const STATUS_OPTIONS = Object.entries(statusConfig).map(([value, cfg]) => ({ value, label: cfg.label }));

// expiry values are named predicates on the API (controllers/invitation.js); "expiring"
// matches the dashboard's "expire within 48 hours" signal.
const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '—');

const InvitationsPage = () => {
    const { loading: loadingApps, selectedAppId: appId, selectedApp: currentApp } = useApps();
    const notice = useNotice();
    const confirm = useConfirm();
    const [error, setError] = useState('');
    const [showSendModal, setShowSendModal] = useState(false);
    const [formData, setFormData] = useState({ email: '', role_ids: [], tenant_id: '' });
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [bulkEmails, setBulkEmails] = useState('');
    const [bulkRoleIds, setBulkRoleIds] = useState([]);
    const [bulkTenantId, setBulkTenantId] = useState('');
    // AIQ-442: a tenant admin's invitations always join their own tenant (the server enforces
    // it); a portal admin picks the tenant for a multi-tenant app. The token is read for
    // display only — never for authorisation.
    const isMultiTenant = currentApp?.is_multi_tenant === 1 || currentApp?.is_multi_tenant === '1';
    const ownTenantId = decodeJwtPayload(getToken())?.tenant_id || '';
    const showTenantPicker = isMultiTenant && !ownTenantId;
    const tenantOptions = useTenants(appId, { enabled: showTenantPicker }).items.map((t) => ({
        value: t.tenant_id,
        label: t.tenant_name,
    }));
    const pickedTenantId = showBulkModal ? bulkTenantId : formData.tenant_id;
    // Roles offered in the invite forms belong to the selected app and the invitation's tenant
    // (application roles plus that tenant's own). Flag roles are set on the user form after
    // acceptance, never offered as roles.
    const rolesQuery = useRoles(appId, ownTenantId || pickedTenantId || null);
    const roles = useMemo(() => splitRoles(rolesQuery.items).roles, [rolesQuery.items]);
    const tenantMissing = (tenantId) => showTenantPicker && !tenantId;
    const [bulkSending, setBulkSending] = useState(false);
    const [bulkResults, setBulkResults] = useState(null);

    // URL keys: status, expiry, created_from/created_to.
    const columns = [
        {
            key: 'email',
            header: 'Email',
            sortKey: 'email',
            locked: true,
            render: (inv) => <span className="font-medium text-slate-900">{inv.email}</span>,
        },
        {
            key: 'status',
            header: 'Status',
            sortKey: 'status',
            render: (inv) => {
                const status = statusConfig[inv.status] ? inv.status : 'pending';
                const cfg = statusConfig[status];
                return <StatusBadge status={status} label={cfg.label} icon={cfg.icon} />;
            },
            filter: { type: 'multiselect', options: STATUS_OPTIONS, defaultExpanded: true },
        },
        {
            key: 'invited_by',
            header: 'Invited by',
            sortKey: 'invited_by',
            render: (inv) => inv.invited_by_name || inv.invited_by_username || '—',
        },
        {
            key: 'created_on',
            header: 'Sent',
            sortKey: 'created',
            render: (inv) => formatDate(inv.created_on),
            filter: { key: 'created', type: 'date' },
        },
        {
            key: 'token_expiry',
            header: 'Expires',
            sortKey: 'expires',
            render: (inv) => formatDate(inv.token_expiry),
            // expiry values are named predicates on the API (controllers/invitation.js); "expiring"
            // matches the dashboard's "expire within 48 hours" signal.
            filter: {
                key: 'expiry',
                type: 'multiselect',
                defaultExpanded: true,
                options: [
                    { value: 'expiring', label: 'Pending, expires within 48 hours' },
                    { value: 'expired', label: 'Past expiry' },
                ],
            },
        },
        {
            key: 'actions',
            header: <span className="sr-only">Actions</span>,
            label: 'Row actions',
            locked: true,
            align: 'right',
            render: (inv) =>
                inv.status === 'pending' ? (
                    <RowActions>
                        <RowAction
                            icon={Ban}
                            label="Revoke invitation"
                            tone="danger"
                            onClick={() => handleRevoke(inv)}
                        />
                    </RowActions>
                ) : null,
        },
    ];

    const table = useServerTable((query) => invitationsService.getPage(appId, query), {
        deps: [appId],
        enabled: Boolean(appId),
        defaultSort: { key: 'created', dir: 'desc' },
        columns,
    });

    const handleSend = async (e) => {
        e.preventDefault();
        setError('');
        try {
            await invitationsService.send(appId, {
                email: formData.email,
                role_ids: formData.role_ids,
                ...(formData.tenant_id && { tenant_id: formData.tenant_id }),
            });
            setShowSendModal(false);
            setFormData({ email: '', role_ids: [], tenant_id: '' });
            table.reload();
            notice.success(`Invitation sent to ${formData.email}.`);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to send invitation');
        }
    };

    const handleRevoke = async (inv) => {
        const ok = await confirm({
            title: `Revoke the invitation to ${inv.email}?`,
            body: 'The invitation link stops working immediately. You can send a new invitation later.',
            confirmLabel: 'Revoke',
            tone: 'danger',
        });
        if (!ok) return;
        try {
            await invitationsService.revoke(inv.invitation_id);
            table.reload();
            notice.success(`Invitation to ${inv.email} revoked.`);
        } catch (err) {
            notice.error(err, `Couldn’t revoke the invitation to ${inv.email}. Try again.`);
        }
    };

    const handleBulkSend = async (e) => {
        e.preventDefault();
        setError('');
        setBulkResults(null);
        setBulkSending(true);

        // Parse emails: split by newlines, commas, or semicolons
        const emails = bulkEmails
            .split(/[\n,;]+/)
            .map((e) => e.trim())
            .filter((e) => e.length > 0);

        if (emails.length === 0) {
            setError('Please enter at least one email address.');
            setBulkSending(false);
            return;
        }

        const invitations = emails.map((email) => ({
            email,
            role_ids: bulkRoleIds,
            ...(bulkTenantId && { tenant_id: bulkTenantId }),
        }));

        try {
            const result = await invitationsService.bulkSend(appId, { invitations });
            setBulkResults(result?.data || result);
            if (result?.data?.succeeded > 0 || result?.succeeded > 0) {
                table.reload();
            }
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to send bulk invitations');
        } finally {
            setBulkSending(false);
        }
    };

    const closeBulkModal = () => {
        setShowBulkModal(false);
        setBulkEmails('');
        setBulkRoleIds([]);
        setBulkTenantId('');
        setBulkResults(null);
        setError('');
    };


    return (
        <div className="space-y-4">
            <PageHeader
                title="Invitations"
                actions={
                    appId && (
                        <>
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setBulkEmails('');
                                    setBulkRoleIds([]);
                                    setBulkTenantId('');
                                    setBulkResults(null);
                                    setError('');
                                    setShowBulkModal(true);
                                }}
                            >
                                <UsersIcon className="h-4 w-4" aria-hidden="true" /> Bulk Invite
                            </Button>
                            <Button
                                onClick={() => {
                                    setFormData({ email: '', role_ids: [], tenant_id: '' });
                                    setError('');
                                    setShowSendModal(true);
                                }}
                            >
                                <Send className="h-4 w-4" aria-hidden="true" /> Send Invite
                            </Button>
                        </>
                    )
                }
            />

            {loadingApps ? (
                <SkeletonList />
            ) : !appId ? (
                <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
                    <p className="text-slate-600">Choose an app in the sidebar to see its invitations.</p>
                </div>
            ) : (
                <DataTable
                    {...table.tableProps}
                    columns={columns}
                    tableKey="invitations"
                    rowKey="invitation_id"
                    searchPlaceholder="Search email or inviter…"
                    emptyMessage="No invitations yet. Send one to get started."
                />
            )}

            {/* Send Invitation Modal */}
            <Modal isOpen={showSendModal} onClose={() => setShowSendModal(false)} title="Send Invitation" size="md">
                <form onSubmit={handleSend} className="space-y-4">
                    <Field label="Email *">
                        <TextInput
                            type="email"
                            required
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            placeholder="user@example.com"
                        />
                    </Field>
                    {showTenantPicker && (
                        <Field label="Tenant *">
                            <Select
                                required
                                value={formData.tenant_id}
                                placeholder="Select a tenant"
                                options={tenantOptions}
                                // Roles depend on the tenant: a new tenant starts a new role pick.
                                onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value, role_ids: [] })}
                            />
                        </Field>
                    )}
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Roles *</label>
                        <div className="space-y-2 max-h-32 overflow-y-auto border border-slate-200 rounded-lg p-2">
                            {roles
                                .filter((r) => r.is_active)
                                .map((role) => (
                                    <label key={role.role_id} className="flex items-center gap-2 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={formData.role_ids.includes(role.role_id)}
                                            onChange={(e) => {
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    role_ids: e.target.checked
                                                        ? [...prev.role_ids, role.role_id]
                                                        : prev.role_ids.filter((id) => id !== role.role_id),
                                                }));
                                            }}
                                            className="rounded border-slate-300"
                                        />
                                        <span>{role.role_name}</span>
                                    </label>
                                ))}
                        </div>
                    </div>

                    {(error || rolesQuery.error) && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error || rolesQuery.error}
                        </div>
                    )}

                    <div className="flex gap-3 pt-4 border-t border-slate-200">
                        <button
                            type="submit"
                            disabled={
                                !formData.email || formData.role_ids.length === 0 || tenantMissing(formData.tenant_id)
                            }
                            className="flex-1 rounded-lg bg-primary-600 text-white font-medium py-2.5 hover:bg-primary-700 disabled:opacity-50"
                        >
                            Send Invitation
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowSendModal(false)}
                            className="flex-1 rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Bulk Invite Modal */}
            <Modal isOpen={showBulkModal} onClose={closeBulkModal} title="Bulk Invite" size="lg">
                <form onSubmit={handleBulkSend} className="space-y-4">
                    <Field
                        label={
                            <>
                                Email Addresses *{' '}
                                <span className="text-xs text-slate-400 font-normal">
                                    (one per line, or comma/semicolon separated, max 100)
                                </span>
                            </>
                        }
                    >
                        <Textarea
                            required
                            rows={6}
                            value={bulkEmails}
                            onChange={(e) => setBulkEmails(e.target.value)}
                            className="font-mono"
                            placeholder={'user1@example.com\nuser2@example.com\nuser3@example.com'}
                        />
                    </Field>
                    {showTenantPicker && (
                        <Field label="Tenant *">
                            <Select
                                required
                                value={bulkTenantId}
                                placeholder="Select a tenant"
                                options={tenantOptions}
                                onChange={(e) => {
                                    setBulkTenantId(e.target.value);
                                    setBulkRoleIds([]);
                                }}
                            />
                        </Field>
                    )}
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            Roles *{' '}
                            <span className="text-xs text-slate-400 font-normal">(applied to all invitations)</span>
                        </label>
                        <div className="space-y-2 max-h-32 overflow-y-auto border border-slate-200 rounded-lg p-2">
                            {roles
                                .filter((r) => r.is_active)
                                .map((role) => (
                                    <label key={role.role_id} className="flex items-center gap-2 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={bulkRoleIds.includes(role.role_id)}
                                            onChange={(e) => {
                                                setBulkRoleIds((prev) =>
                                                    e.target.checked
                                                        ? [...prev, role.role_id]
                                                        : prev.filter((id) => id !== role.role_id)
                                                );
                                            }}
                                            className="rounded border-slate-300"
                                        />
                                        <span>{role.role_name}</span>
                                    </label>
                                ))}
                        </div>
                    </div>

                    {(error || rolesQuery.error) && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error || rolesQuery.error}
                        </div>
                    )}

                    {/* Bulk Results Display */}
                    {bulkResults && (
                        <div className="space-y-2">
                            <div className="flex gap-3">
                                <div className="flex-1 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 text-sm font-medium text-center">
                                    {bulkResults.succeeded} succeeded
                                </div>
                                {bulkResults.failed?.length > 0 && (
                                    <div className="flex-1 rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm font-medium text-center">
                                        {bulkResults.failed.length} failed
                                    </div>
                                )}
                            </div>
                            {bulkResults.failed?.length > 0 && (
                                <div className="rounded-lg border border-red-100 bg-red-50 p-3 max-h-40 overflow-y-auto">
                                    <p className="text-xs font-semibold text-red-700 mb-1">Failed invitations:</p>
                                    {bulkResults.failed.map((f, idx) => (
                                        <div key={idx} className="text-xs text-red-600 py-0.5">
                                            <span className="font-medium">{f.email}</span>: {f.error}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="flex gap-3 pt-4 border-t border-slate-200">
                        {!bulkResults ? (
                            <>
                                <button
                                    type="submit"
                                    disabled={
                                        !bulkEmails.trim() ||
                                        bulkRoleIds.length === 0 ||
                                        bulkSending ||
                                        tenantMissing(bulkTenantId)
                                    }
                                    className="flex-1 rounded-lg bg-primary-600 text-white font-medium py-2.5 hover:bg-primary-700 disabled:opacity-50"
                                >
                                    {bulkSending ? 'Sending...' : 'Send All'}
                                </button>
                                <button
                                    type="button"
                                    onClick={closeBulkModal}
                                    className="flex-1 rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                                >
                                    Cancel
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                onClick={closeBulkModal}
                                className="flex-1 rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                            >
                                Close
                            </button>
                        )}
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default InvitationsPage;
